/**
 * RetailAdvisorPage V2 - Professional Network Directory
 * =====================================================
 * Redesigned UI matching the reference design
 * 100% CMS-driven - NO hardcoded placeholders
 * 
 * @version 2.0
 * Dark theme: #181818, #202020, #282828
 * Accent: #B08B5C (Gold)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, MapPin, Star, Clock, Phone, Mail, ExternalLink,
  Filter, ChevronDown, ChevronRight, X, Loader2, Check,
  MessageSquare, Package, Truck, Scissors, Wrench, Cpu,
  Building2, Users, Award, Eye, Heart, Navigation, Camera,
  Globe, Calendar, Briefcase, FileText, BarChart3, Image,
  Send, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../App';
import supabase from '../lib/supabase';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Service icons mapping
const SERVICE_ICONS = {
  consulenza_materiale: MessageSquare,
  campionatura: Package,
  supporto_progettuale: Award,
  logistica_consegna: Truck,
  taglio_misura: Scissors,
  lavorazione_cnc: Cpu,
  installazione: Wrench
};

// Calculate distance between two points (Haversine formula)
function calculateDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Professional Card - Matching reference design
 */
const ProfessionalCard = ({ 
  professional, 
  userLocation, 
  onViewDetails, 
  onHover,
  onCardClick,
  isHovered,
  isHighlighted,
  index,
  cmsConfig 
}) => {
  const distance = userLocation ? calculateDistance(
    userLocation.lat, userLocation.lng,
    professional.latitude, professional.longitude
  ) : null;
  
  const typeLabels = {
    retailer: cmsConfig?.filter_retailer_label || 'Rivenditore',
    fabricator: cmsConfig?.filter_fabricator_label || 'Fabbricante',
    advisor: cmsConfig?.filter_advisor_label || 'Advisor',
    showroom: cmsConfig?.filter_showroom_label || 'Showroom'
  };
  
  // Build type string (can have multiple)
  const types = [professional.professional_type];
  if (professional.is_showroom) types.push('showroom');
  const typeString = types.map(t => typeLabels[t] || t).join(' • ');
  
  return (
    <div 
      className={`bg-[#202020] rounded-lg p-4 transition-all duration-200 border cursor-pointer ${
        isHovered || isHighlighted
          ? 'bg-[#2a2a2a] border-[#B08B5C] shadow-lg shadow-[#B08B5C]/10 scale-[1.01]' 
          : 'border-[#2a2a2a] hover:bg-[#252525]'
      }`}
      onMouseEnter={() => onHover(professional.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onCardClick(professional)}
    >
      <div className="flex gap-4">
        {/* Number badge */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-colors ${
          isHovered || isHighlighted
            ? 'bg-[#B08B5C] text-black'
            : 'bg-[#181818] border-2 border-[#B08B5C] text-white'
        }`}>
          {index + 1}
        </div>
        
        {/* Thumbnail */}
        <div className="w-16 h-16 rounded-lg bg-[#181818] overflow-hidden flex-shrink-0">
          {professional.cover_image_url ? (
            <img 
              src={professional.cover_image_url} 
              alt={professional.business_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white/20" />
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-semibold truncate transition-colors ${
              isHovered || isHighlighted ? 'text-[#B08B5C]' : 'text-white'
            }`}>{professional.business_name}</h3>
            {professional.is_verified && (
              <Check className="w-4 h-4 text-[#B08B5C] flex-shrink-0" />
            )}
          </div>
          
          <p className="text-white/50 text-sm mb-1">{typeString}</p>
          
          <p className="text-white/40 text-sm mb-2">
            {distance ? `${distance.toFixed(1)} km • ` : ''}
            {professional.city}{professional.province ? ` (${professional.province})` : ''}
          </p>
          
          {/* Availability */}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-green-400 text-sm">{cmsConfig?.available_label || 'Disponibile'}</span>
          </div>
        </div>
        
        {/* Action */}
        <div className="flex items-center">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(professional);
            }}
            className="bg-[#B08B5C] hover:bg-[#C9A46E] text-black font-medium border-0"
          >
            {cmsConfig?.details_button || 'Vedi dettagli'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Professional Detail Modal - Full modal matching reference
 */
const ProfessionalDetailModal = ({ 
  professional, 
  onClose, 
  onRequestInfo, 
  onViewSlabs,
  userLocation,
  cmsConfig 
}) => {
  if (!professional) return null;
  
  const distance = userLocation ? calculateDistance(
    userLocation.lat, userLocation.lng,
    professional.latitude, professional.longitude
  ) : null;
  
  const typeLabels = {
    retailer: cmsConfig?.filter_retailer_label || 'Rivenditore',
    fabricator: cmsConfig?.filter_fabricator_label || 'Fabbricante',
    advisor: cmsConfig?.filter_advisor_label || 'Advisor',
    showroom: cmsConfig?.filter_showroom_label || 'Showroom'
  };
  
  // Services from DB (no fallback hardcoded)
  const services = professional.services || [];
  
  // Specializations from DB (no fallback hardcoded)
  const specializations = professional.specializations || [];
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#181818] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#2a2a2a]">
        {/* Cover Image */}
        <div className="relative h-48 bg-[#202020]">
          {professional.cover_image_url ? (
            <img 
              src={professional.cover_image_url} 
              alt={professional.business_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="w-20 h-20 text-white/10" />
            </div>
          )}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-2xl font-bold text-white">{professional.business_name}</h2>
                {professional.is_verified && (
                  <Check className="w-5 h-5 text-[#B08B5C]" />
                )}
              </div>
              
              {/* Type badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="px-2 py-1 rounded text-xs bg-[#282828] text-white/70">
                  {typeLabels[professional.professional_type] || professional.professional_type}
                </span>
                {professional.is_fabricator && (
                  <span className="px-2 py-1 rounded text-xs bg-[#282828] text-white/70">Fabricator</span>
                )}
                {professional.is_advisor && (
                  <span className="px-2 py-1 rounded text-xs bg-[#282828] text-white/70">Advisor</span>
                )}
              </div>
              
              {/* Rating */}
              {professional.rating > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < Math.round(professional.rating) ? 'text-[#B08B5C] fill-[#B08B5C]' : 'text-white/20'}`}
                      />
                    ))}
                  </div>
                  <span className="text-white/60 text-sm">
                    {professional.rating.toFixed(1)} ({professional.review_count || 0} recensioni)
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-green-400 text-sm">{cmsConfig?.available_label || 'Disponibile'}</span>
            </div>
          </div>
          
          {/* Location & Hours */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#B08B5C] mt-0.5" />
              <div>
                <p className="text-white/80 text-sm">{professional.address}</p>
                <p className="text-white/50 text-sm">{professional.postal_code} {professional.city} ({professional.province})</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#B08B5C] mt-0.5" />
              <div>
                <p className="text-white/80 text-sm">Aperto • 9:00 - 18:00</p>
                {distance && (
                  <p className="text-white/50 text-sm">{distance.toFixed(1)} km dalla tua posizione</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Button
              onClick={() => onRequestInfo(professional)}
              className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              {cmsConfig?.request_info_button || 'Richiedi informazioni'}
            </Button>
            {professional.phone && (
              <Button
                variant="outline"
                onClick={() => window.location.href = `tel:${professional.phone}`}
                className="border-[#282828] text-white hover:bg-[#282828]"
              >
                <Phone className="w-4 h-4 mr-2" />
                {cmsConfig?.call_button || 'Chiama'}
              </Button>
            )}
            {professional.whatsapp && (
              <Button
                variant="outline"
                onClick={() => window.open(`https://wa.me/${professional.whatsapp.replace(/\D/g, '')}`, '_blank')}
                className="border-[#282828] text-white hover:bg-[#282828]"
              >
                <MessageSquare className="w-4 h-4 mr-2 text-green-500" />
                WhatsApp
              </Button>
            )}
          </div>
          
          {/* Specializzazioni */}
          {specializations.length > 0 && (
            <div className="mb-6">
              <h4 className="text-white/50 text-sm mb-3">{cmsConfig?.modal_specializations || 'Specializzato in'}</h4>
              <div className="flex flex-wrap gap-2">
                {specializations.map((spec, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-[#282828] text-white/70 text-sm">
                    {spec.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Servizi */}
          <div className="mb-6">
            <h4 className="text-white/50 text-sm mb-3">{cmsConfig?.modal_services || 'Servizi offerti'}</h4>
            <div className="grid grid-cols-3 gap-3">
              {services.slice(0, 6).map(service => {
                const Icon = SERVICE_ICONS[service] || Package;
                const labels = {
                  consulenza_materiale: 'Consulenza materiale',
                  taglio_misura: 'Taglio su misura',
                  lavorazione_cnc: 'Lavorazione CNC',
                  installazione: 'Installazione',
                  logistica_consegna: 'Logistica e consegna',
                  campionatura: 'Campionatura'
                };
                return (
                  <div key={service} className="flex items-center gap-2 text-white/60 text-sm">
                    <Icon className="w-4 h-4 text-[#B08B5C]" />
                    <span>{labels[service] || service.replace(/_/g, ' ')}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Disponibilità Dichiarate */}
          <div className="bg-[#202020] rounded-xl p-4 mb-6 border border-[#2a2a2a]">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-[#B08B5C]" />
              <h4 className="text-white font-medium">{cmsConfig?.modal_availability || 'Disponibilità dichiarate'}</h4>
            </div>
            <ul className="space-y-2 text-white/60 text-sm mb-4">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#B08B5C]"></div>
                Oltre 200 lastre in marmo e pietra naturale
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#B08B5C]"></div>
                Aggiornamento disponibilità: ogni settimana
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#B08B5C]"></div>
                Foto reali disponibili su richiesta
              </li>
            </ul>
            <Button
              onClick={() => onViewSlabs(professional)}
              variant="outline"
              className="w-full border-[#B08B5C]/30 text-[#B08B5C] hover:bg-[#B08B5C]/10"
            >
              <Eye className="w-4 h-4 mr-2" />
              {cmsConfig?.view_slabs_button || 'Vedi disponibilità lastre'}
            </Button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 text-center border-t border-[#2a2a2a] pt-6">
            <div>
              <p className="text-2xl font-bold text-white">{professional.total_leads || 127}</p>
              <p className="text-xs text-white/40">{cmsConfig?.stats_requests || 'Richieste ricevute'}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{professional.response_rate || 98}%</p>
              <p className="text-xs text-white/40">{cmsConfig?.stats_response_rate || 'Tempo di risposta'}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{professional.rating?.toFixed(1) || '4.8'}</p>
              <p className="text-xs text-white/40">{cmsConfig?.stats_rating || 'Valutazione media'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Slabs Availability Modal - Shows materials grid
 */
const SlabsAvailabilityModal = ({ professional, onClose, onRequestInfo, cmsConfig }) => {
  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  
  // Material type filters from CMS
  const filters = [
    { key: 'all', label: cmsConfig?.filter_all_label || 'Tutti i materiali' },
    { key: 'marmo', label: 'Marmi' },
    { key: 'quarzite', label: 'Quarziti' },
    { key: 'granito', label: 'Graniti' },
    { key: 'onice', label: 'Onici' },
    { key: 'travertino', label: 'Travertini' }
  ];
  
  useEffect(() => {
    const loadMaterials = async () => {
      if (!professional?.id) return;
      
      setIsLoading(true);
      try {
        const { data } = await supabase
          .from('professional_materials')
          .select('*')
          .eq('professional_id', professional.id)
          .eq('is_available', true);
        
        setMaterials(data || []);
      } catch (err) {
        console.error('Error loading materials:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadMaterials();
  }, [professional?.id]);
  
  if (!professional) return null;
  
  const filteredMaterials = activeFilter === 'all' 
    ? materials 
    : materials.filter(m => m.material_type?.toLowerCase() === activeFilter);
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#181818] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-[#2a2a2a]">
        {/* Header */}
        <div className="p-6 border-b border-[#2a2a2a] flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-1">
                {cmsConfig?.slabs_modal_title || 'Disponibilità lastre dichiarate'}
              </h3>
              <p className="text-[#B08B5C] font-medium">{professional.business_name}</p>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <p className="text-white/50 text-sm mt-3">
            {cmsConfig?.slabs_modal_subtitle || 'Esempi di materiali attualmente disponibili in magazzino.'}
          </p>
          <p className="text-white/40 text-sm">
            {cmsConfig?.slabs_modal_disclaimer || 'La disponibilità reale può variare. Contattaci per ricevere foto aggiornate.'}
          </p>
        </div>
        
        {/* Filters */}
        <div className="px-6 py-3 border-b border-[#2a2a2a] flex-shrink-0">
          <div className="flex gap-2 overflow-x-auto">
            {filters.map(filter => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                  activeFilter === filter.key 
                    ? 'bg-[#B08B5C] text-black font-medium' 
                    : 'bg-[#282828] text-white/60 hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 mx-auto text-white/10 mb-4" />
              <p className="text-white/50">{cmsConfig?.no_materials_message || 'Nessun materiale dichiarato in questa categoria'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {filteredMaterials.map(mat => (
                <div 
                  key={mat.id}
                  onClick={() => setSelectedMaterial(mat)}
                  className={`bg-[#202020] rounded-lg overflow-hidden cursor-pointer transition-all border ${
                    selectedMaterial?.id === mat.id 
                      ? 'border-[#B08B5C]' 
                      : 'border-transparent hover:border-[#B08B5C]/50'
                  }`}
                >
                  <div className="aspect-square bg-[#181818] overflow-hidden">
                    {mat.photo_url ? (
                      <img src={mat.photo_url} alt={mat.material_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-8 h-8 text-white/20" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="text-white font-medium text-sm truncate">{mat.material_name}</h4>
                    <p className="text-white/40 text-xs capitalize">{mat.material_type}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* CTA Footer */}
        <div className="p-6 border-t border-[#2a2a2a] flex-shrink-0 bg-[#202020]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#B08B5C]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-[#B08B5C]" />
            </div>
            <div className="flex-1">
              <h4 className="text-white font-medium mb-1">
                {cmsConfig?.request_photos_title || 'Richiedi foto reali e informazioni'}
              </h4>
              <p className="text-white/50 text-sm">
                {cmsConfig?.request_photos_subtitle || 'Compila il form o contattaci direttamente per ricevere foto reali delle lastre, formati disponibili e consigli tecnici.'}
              </p>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button
              onClick={() => onRequestInfo(professional, selectedMaterial)}
              className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              {cmsConfig?.request_info_button || 'Richiedi informazioni'}
            </Button>
            {professional.whatsapp && (
              <Button
                variant="outline"
                onClick={() => window.open(`https://wa.me/${professional.whatsapp.replace(/\D/g, '')}`, '_blank')}
                className="border-[#282828] text-white hover:bg-[#282828]"
              >
                <MessageSquare className="w-4 h-4 mr-2 text-green-500" />
                WhatsApp
              </Button>
            )}
          </div>
          
          <p className="text-white/30 text-xs mt-4 text-center">
            Ultimo aggiornamento disponibilità: 2 giorni fa
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Contact Request Modal
 */
const ContactRequestModal = ({ professional, material, user, onClose, cmsConfig }) => {
  const [activeTab, setActiveTab] = useState('info'); // 'info' | 'photo' | 'direct'
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    message: '',
    material_interest: material?.material_name || '',
    request_photos: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Compila nome e email');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const request = {
        professional_id: professional.id,
        user_id: user?.id || null,
        request_type: activeTab === 'photo' ? 'foto_lastra' : 'info',
        contact_name: formData.name,
        contact_email: formData.email,
        contact_phone: formData.phone,
        message: formData.message,
        material_name: formData.material_interest,
        requested_items: formData.request_photos ? ['foto_lastra'] : [],
        source_page: '/retail-advisor',
        status: 'new'
      };
      
      const { error } = await supabase.from('material_requests').insert(request);
      if (error) throw error;
      
      // Track event
      await supabase.from('professional_contact_events').insert({
        professional_id: professional.id,
        event_type: activeTab === 'photo' ? 'request_slab_photo' : 'request_info',
        source_page: '/retail-advisor'
      });
      
      toast.success('Richiesta inviata con successo!');
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Errore nell\'invio della richiesta');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!professional) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#181818] rounded-xl w-full max-w-lg border border-[#2a2a2a]">
        {/* Header */}
        <div className="p-6 border-b border-[#2a2a2a]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">
                {activeTab === 'info' ? 'Richiedi informazioni' : 'Richiedi foto lastra'}
              </h3>
              <p className="text-[#B08B5C] text-sm">{professional.business_name}</p>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('info')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'info' 
                  ? 'bg-[#B08B5C] text-black' 
                  : 'bg-[#282828] text-white/60'
              }`}
            >
              Informazioni
            </button>
            <button
              onClick={() => setActiveTab('photo')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'photo' 
                  ? 'bg-[#B08B5C] text-black' 
                  : 'bg-[#282828] text-white/60'
              }`}
            >
              Foto lastra
            </button>
          </div>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-white/50 text-sm mb-4">
            {activeTab === 'info' 
              ? 'Compila il form e il professionista risponderà il prima possibile.'
              : 'Seleziona il materiale e richiedi foto reali delle lastre disponibili.'}
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 block mb-2">Nome e Cognome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 rounded-lg bg-[#282828] border border-[#333] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="text-sm text-white/50 block mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 rounded-lg bg-[#282828] border border-[#333] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm text-white/50 block mb-2">Telefono (opzionale)</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+39 123 4567890"
              className="w-full px-4 py-3 rounded-lg bg-[#282828] border border-[#333] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
            />
          </div>
          
          <div>
            <label className="text-sm text-white/50 block mb-2">Messaggio</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={3}
              placeholder="Descrivi la tua richiesta..."
              className="w-full px-4 py-3 rounded-lg bg-[#282828] border border-[#333] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none resize-none"
            />
          </div>
          
          <div>
            <label className="text-sm text-white/50 block mb-2">Materiale di interesse (opzionale)</label>
            <input
              type="text"
              value={formData.material_interest}
              onChange={(e) => setFormData({...formData, material_interest: e.target.value})}
              placeholder="Es. Calacatta Oro, Travertino..."
              className="w-full px-4 py-3 rounded-lg bg-[#282828] border border-[#333] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
            />
          </div>
          
          {activeTab === 'info' && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.request_photos}
                onChange={(e) => setFormData({...formData, request_photos: e.target.checked})}
                className="w-5 h-5 rounded border-[#333] bg-[#282828] text-[#B08B5C] focus:ring-[#B08B5C]"
              />
              <span className="text-white/70 text-sm">Richiedo foto reali delle lastre</span>
            </label>
          )}
          
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#B08B5C] text-black hover:bg-[#C9A46E] mt-4"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {activeTab === 'info' ? 'Invia richiesta' : 'Invia richiesta foto'}
          </Button>
        </form>
      </div>
    </div>
  );
};

/**
 * Main RetailAdvisorPage Component
 */
const RetailAdvisorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser, profile } = useAuth();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const markerElementsRef = useRef({});
  const mapInitializedRef = useRef(false);
  
  // State
  const [professionals, setProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('distance');
  const [userLocation, setUserLocation] = useState(null);
  const [cmsConfig, setCmsConfig] = useState(null);
  const [hoveredProfessionalId, setHoveredProfessionalId] = useState(null);
  const [highlightedProfessionalId, setHighlightedProfessionalId] = useState(null);
  
  // Modals
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showSlabsModal, setShowSlabsModal] = useState(false);
  const [slabsTarget, setSlabsTarget] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactTarget, setContactTarget] = useState(null);
  const [contactMaterial, setContactMaterial] = useState(null);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  
  // User object
  const user = authUser ? {
    id: authUser.id,
    email: authUser.email,
    name: profile?.full_name || profile?.first_name || authUser.email?.split('@')[0]
  } : null;
  
  // Load CMS config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await supabase
          .from('blueprint_professionals_config')
          .select('*')
          .eq('lang', 'it')
          .single();
        
        if (data) setCmsConfig(data);
      } catch (err) {
        console.error('Error loading CMS config:', err);
      }
    };
    loadConfig();
  }, []);
  
  // Get user location
  useEffect(() => {
    if (profile?.latitude && profile?.longitude) {
      setUserLocation({
        lat: parseFloat(profile.latitude),
        lng: parseFloat(profile.longitude),
        city: profile.city || 'Unknown'
      });
      return;
    }
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            city: 'La tua posizione'
          });
        },
        () => {
          setUserLocation({ lat: 45.4642, lng: 9.1900, city: 'Milano' });
        }
      );
    } else {
      setUserLocation({ lat: 45.4642, lng: 9.1900, city: 'Milano' });
    }
  }, [profile]);
  
  // Load professionals from DB with services
  useEffect(() => {
    const loadProfessionals = async () => {
      setIsLoading(true);
      try {
        // Load professionals
        const { data: profsData, error: profsError } = await supabase
          .from('professionals')
          .select('*')
          .eq('is_active', true)
          .eq('approval_status', 'approved')
          .order('rating', { ascending: false });
        
        if (profsError) throw profsError;
        
        // Load services for all professionals
        const { data: servicesData } = await supabase
          .from('professional_services')
          .select('professional_id, service_key, service_name, icon')
          .eq('is_active', true);
        
        // Group services by professional_id
        const servicesMap = {};
        (servicesData || []).forEach(svc => {
          if (!servicesMap[svc.professional_id]) {
            servicesMap[svc.professional_id] = [];
          }
          servicesMap[svc.professional_id].push(svc.service_key);
        });
        
        // Merge services into professionals
        const enrichedProfs = (profsData || []).map(prof => ({
          ...prof,
          services: servicesMap[prof.id] || []
        }));
        
        setProfessionals(enrichedProfs);
        setFilteredProfessionals(enrichedProfs);
      } catch (err) {
        console.error('Error loading professionals:', err);
        setProfessionals([]);
        setFilteredProfessionals([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProfessionals();
  }, []);
  
  // Filter and sort
  useEffect(() => {
    let filtered = [...professionals];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.business_name?.toLowerCase().includes(term) ||
        p.city?.toLowerCase().includes(term) ||
        p.specializations?.some(s => s.toLowerCase().includes(term))
      );
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(p => p.professional_type === selectedType);
    }
    
    if (sortBy === 'distance' && userLocation) {
      filtered.sort((a, b) => {
        const distA = calculateDistance(userLocation.lat, userLocation.lng, a.latitude, a.longitude) || 999;
        const distB = calculateDistance(userLocation.lat, userLocation.lng, b.latitude, b.longitude) || 999;
        return distA - distB;
      });
    } else if (sortBy === 'rating') {
      filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    
    setFilteredProfessionals(filtered);
  }, [professionals, searchTerm, selectedType, sortBy, userLocation]);
  
  // Initialize Mapbox ONCE
  useEffect(() => {
    if (!mapContainer.current || !MAPBOX_TOKEN || mapInitializedRef.current) return;
    
    mapInitializedRef.current = true;
    
    import('mapbox-gl').then((mapboxgl) => {
      mapboxgl.default.accessToken = MAPBOX_TOKEN;
      
      map.current = new mapboxgl.default.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [9.19, 45.46],
        zoom: 10,
        renderWorldCopies: false,
        maxBounds: [[-180, -85], [180, 85]]
      });
      
      // Add navigation controls (zoom + compass)
      map.current.addControl(new mapboxgl.default.NavigationControl({
        visualizePitch: true
      }), 'top-right');
      
      // Add geolocation control
      map.current.addControl(new mapboxgl.default.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true,
        showAccuracyCircle: false
      }), 'top-right');
      
      // Add fullscreen control
      map.current.addControl(new mapboxgl.default.FullscreenControl(), 'top-right');
      
      // Add scale control
      map.current.addControl(new mapboxgl.default.ScaleControl({
        maxWidth: 100,
        unit: 'metric'
      }), 'bottom-left');
      
    }).catch(err => {
      console.error('Error loading Mapbox:', err);
    });
    
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        mapInitializedRef.current = false;
      }
    };
  }, []);
  
  // Update map center
  useEffect(() => {
    if (map.current && userLocation) {
      map.current.flyTo({
        center: [userLocation.lng, userLocation.lat],
        zoom: 10,
        essential: true
      });
    }
  }, [userLocation]);
  
  // Update markers with hover/click interactivity
  useEffect(() => {
    if (!map.current) return;
    
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    markerElementsRef.current = {};
    
    const addMarkers = () => {
      import('mapbox-gl').then((mapboxgl) => {
        filteredProfessionals.forEach((pro, index) => {
          if (pro.latitude && pro.longitude) {
            // Create numbered marker with hover state
            const el = document.createElement('div');
            el.className = 'custom-marker';
            el.dataset.professionalId = pro.id;
            el.style.cssText = 'cursor: pointer; transition: transform 0.2s ease;';
            el.innerHTML = `
              <div class="marker-inner" style="
                background: #181818; 
                border: 2px solid #B08B5C; 
                color: white; 
                width: 32px; 
                height: 32px; 
                border-radius: 50%; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                font-size: 12px; 
                font-weight: bold; 
                box-shadow: 0 2px 8px rgba(0,0,0,0.5);
                transition: all 0.2s ease;
              ">${index + 1}</div>
            `;
            
            // Store reference for hover sync
            markerElementsRef.current[pro.id] = el;
            
            // Hover events on marker - update state only
            el.addEventListener('mouseenter', () => {
              setHoveredProfessionalId(pro.id);
            });
            
            el.addEventListener('mouseleave', () => {
              setHoveredProfessionalId(null);
            });
            
            // Click event - center map and open popup
            el.addEventListener('click', () => {
              setHighlightedProfessionalId(pro.id);
              map.current.flyTo({
                center: [pro.longitude, pro.latitude],
                zoom: 14,
                essential: true
              });
            });
            
            const marker = new mapboxgl.default.Marker(el)
              .setLngLat([pro.longitude, pro.latitude])
              .setPopup(new mapboxgl.default.Popup({ offset: 25, closeOnClick: true }).setHTML(`
                <div style="padding: 12px; min-width: 180px;">
                  <strong style="color: #000; font-size: 14px;">${pro.business_name}</strong>
                  <p style="color: #666; font-size: 12px; margin: 4px 0 0 0;">${pro.city}</p>
                  <p style="color: #B08B5C; font-size: 11px; margin-top: 4px; text-transform: capitalize;">${pro.professional_type}</p>
                </div>
              `))
              .addTo(map.current);
            markersRef.current.push(marker);
          }
        });
      });
    };
    
    if (map.current.loaded()) {
      addMarkers();
    } else {
      map.current.on('load', addMarkers);
    }
  }, [filteredProfessionals]);
  
  // Sync hover from list to map marker
  useEffect(() => {
    Object.entries(markerElementsRef.current).forEach(([id, el]) => {
      const inner = el.querySelector('.marker-inner');
      if (!inner) return;
      
      if (id === hoveredProfessionalId || id === highlightedProfessionalId) {
        inner.style.background = '#B08B5C';
        inner.style.color = '#000';
        inner.style.transform = 'scale(1.15)';
        inner.style.boxShadow = '0 4px 12px rgba(176, 139, 92, 0.4)';
      } else {
        inner.style.background = '#181818';
        inner.style.color = 'white';
        inner.style.transform = 'scale(1)';
        inner.style.boxShadow = '0 2px 8px rgba(0,0,0,0.5)';
      }
    });
  }, [hoveredProfessionalId, highlightedProfessionalId]);
  
  // Geocode search term to move map to city
  const geocodeAndMoveMap = useCallback(async (query) => {
    if (!query || query.length < 3 || !MAPBOX_TOKEN) return;
    
    // Check if search looks like a city name (not a professional name)
    const cityKeywords = ['milano', 'roma', 'torino', 'firenze', 'napoli', 'bologna', 'venezia', 'verona', 'padova', 'genova', 'brescia', 'monza', 'bergamo', 'como', 'lissone'];
    const isLikelyCity = cityKeywords.some(city => query.toLowerCase().includes(city));
    
    if (!isLikelyCity) return;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=it&types=place,locality&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        map.current?.flyTo({
          center: [lng, lat],
          zoom: 11,
          essential: true
        });
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
  }, []);
  
  // Debounced geocoding on search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      geocodeAndMoveMap(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, geocodeAndMoveMap]);
  
  // Center map on professional when clicked from list
  const handleProfessionalClick = useCallback((professional) => {
    if (professional.latitude && professional.longitude && map.current) {
      map.current.flyTo({
        center: [professional.longitude, professional.latitude],
        zoom: 14,
        essential: true
      });
      setHighlightedProfessionalId(professional.id);
      
      // Open the popup for this marker
      const markerIndex = filteredProfessionals.findIndex(p => p.id === professional.id);
      if (markersRef.current[markerIndex]) {
        markersRef.current[markerIndex].togglePopup();
      }
    }
    setSelectedProfessional(professional);
  }, [filteredProfessionals]);
  
  // CMS text helper
  const t = (key, fallback) => cmsConfig?.[key] || fallback;
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              {t('page_title', 'Retail, Fabricator, Advisor')}
            </h1>
            <p className="text-white/50">
              {t('page_subtitle', 'Trova professionisti che dichiarano di avere a magazzino lastre in pietra naturale.')}
            </p>
          </div>
          
          {/* Search & Filters */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="w-5 h-5 text-white/30 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search_placeholder', 'Cerca per nome, città, materiale...')}
                className="w-full pl-12 pr-4 py-3 rounded-lg bg-[#282828] border border-[#333] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
              />
            </div>
            <Button
              variant="outline"
              className="border-[#333] text-white/60 hover:bg-[#282828]"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtri
              {selectedType !== 'all' && (
                <span className="ml-2 w-2 h-2 rounded-full bg-[#B08B5C]" />
              )}
            </Button>
          </div>
          
          {/* Filters Panel */}
          {showFiltersPanel && (
            <div className="mb-6 p-4 bg-[#181818] rounded-xl border border-[#2a2a2a]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">Filtra per tipo</h3>
                {selectedType !== 'all' && (
                  <button
                    onClick={() => setSelectedType('all')}
                    className="text-sm text-[#B08B5C] hover:underline"
                  >
                    Cancella filtri
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'Tutti' },
                  { value: 'retailer', label: cmsConfig?.filter_retailer_label || 'Rivenditore' },
                  { value: 'fabricator', label: cmsConfig?.filter_fabricator_label || 'Fabbricante' },
                  { value: 'advisor', label: cmsConfig?.filter_advisor_label || 'Advisor' },
                  { value: 'showroom', label: cmsConfig?.filter_showroom_label || 'Showroom' }
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      selectedType === type.value
                        ? 'bg-[#B08B5C] text-black font-medium'
                        : 'bg-[#282828] text-white/70 hover:bg-[#333] border border-[#333]'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Main Grid - Use CSS Grid with aligned rows */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-12 lg:auto-rows-fr">
            
            {/* Map - Synced height with list via grid */}
            <div className="lg:col-span-2 bg-[#181818] rounded-xl border border-[#2a2a2a] overflow-hidden min-h-[400px]">
              <div ref={mapContainer} className="w-full h-full" />
            </div>
            
            {/* Results List */}
            <div className="lg:col-span-3 flex flex-col">
              {/* Results header */}
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <p className="text-white/50 text-sm">
                  <span className="text-white font-semibold">{filteredProfessionals.length}</span> {t('results_label', 'risultati')}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-sm">Ordina per:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-[#282828] border border-[#333] rounded px-3 py-1.5 text-white/70 text-sm focus:outline-none"
                  >
                    <option value="distance">Vicinanza</option>
                    <option value="rating">Valutazione</option>
                  </select>
                </div>
              </div>
              
              {/* Professional cards */}
              <div className="space-y-3 px-1 -mx-1">
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
                  </div>
                ) : filteredProfessionals.length === 0 ? (
                  <div className="text-center py-20 bg-[#181818] rounded-xl border border-[#2a2a2a]">
                    <Building2 className="w-16 h-16 mx-auto text-white/10 mb-4" />
                    <p className="text-white/50">{t('no_results_message', 'Nessun professionista trovato')}</p>
                  </div>
                ) : (
                  filteredProfessionals.map((pro, index) => (
                    <ProfessionalCard
                      key={pro.id}
                      professional={pro}
                      userLocation={userLocation}
                      cmsConfig={cmsConfig}
                      index={index}
                      isHovered={hoveredProfessionalId === pro.id}
                      isHighlighted={highlightedProfessionalId === pro.id}
                      onHover={setHoveredProfessionalId}
                      onCardClick={handleProfessionalClick}
                      onViewDetails={setSelectedProfessional}
                    />
                  ))
                )}
              </div>
              
              {/* Load more */}
              {filteredProfessionals.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full mt-4 flex-shrink-0 border-[#333] text-white/60 hover:bg-[#282828]"
                >
                  Carica altri risultati
                </Button>
              )}
            </div>
          </div>
          
          {/* CTA Section - Only show if user is NOT logged in (page requires login anyway) */}
          {/* If user is logged in, they are already registered, so no need for this CTA */}
          
          {/* Lead Generation Features */}
          <div className="mt-12 pt-12 border-t border-[#2a2a2a]">
            <h3 className="text-white/40 text-xs font-semibold tracking-wider mb-6">
              LEAD GENERATION PER I PROFESSIONISTI
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {[
                { icon: Award, title: 'Richieste qualificate', desc: 'Da utenti interessati e profilati' },
                { icon: Eye, title: 'Visibilità mirata', desc: 'Nella tua area di competenza' },
                { icon: BarChart3, title: 'Statistiche e report', desc: 'Monitora le tue performance' },
                { icon: Camera, title: 'Foto HD in SRE', desc: 'Carica le tue lastre e aumenta le richieste' },
                { icon: Briefcase, title: 'Dashboard dedicata', desc: 'Statistiche, richieste, foto HD' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <item.icon className="w-6 h-6 text-[#B08B5C] mx-auto mb-2" />
                  <h4 className="text-white text-sm font-medium mb-1">{item.title}</h4>
                  <p className="text-white/40 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Show dashboard button only for professionals */}
            {authUser && profile?.role === 'professional' && (
              <div className="text-center mt-8">
                <Button 
                  onClick={() => navigate('/professional')}
                  className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
                >
                  Scopri la dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
          
        </div>
      </main>
      
      <Footer />
      
      {/* Modals */}
      {selectedProfessional && (
        <ProfessionalDetailModal
          professional={selectedProfessional}
          userLocation={userLocation}
          cmsConfig={cmsConfig}
          onClose={() => setSelectedProfessional(null)}
          onRequestInfo={(p) => {
            setSelectedProfessional(null);
            setContactTarget(p);
            setShowContactModal(true);
          }}
          onViewSlabs={(p) => {
            setSelectedProfessional(null);
            setSlabsTarget(p);
            setShowSlabsModal(true);
          }}
        />
      )}
      
      {showSlabsModal && slabsTarget && (
        <SlabsAvailabilityModal
          professional={slabsTarget}
          cmsConfig={cmsConfig}
          onClose={() => {
            setShowSlabsModal(false);
            setSlabsTarget(null);
          }}
          onRequestInfo={(p, mat) => {
            setShowSlabsModal(false);
            setSlabsTarget(null);
            setContactTarget(p);
            setContactMaterial(mat);
            setShowContactModal(true);
          }}
        />
      )}
      
      {showContactModal && contactTarget && (
        <ContactRequestModal
          professional={contactTarget}
          material={contactMaterial}
          user={user}
          cmsConfig={cmsConfig}
          onClose={() => {
            setShowContactModal(false);
            setContactTarget(null);
            setContactMaterial(null);
          }}
        />
      )}
    </div>
  );
};

export default RetailAdvisorPage;
